/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\ContactTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { User } from 'lucide-react';

interface ContactTabProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactInfo: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: string, value: any) => void;
}

export function ContactTab({ contactInfo, onChange }: ContactTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  return (
    <div className="space-y-4">
      <FormBox
        title={t('extensions.contact.title')}
        icon={<User className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
          {/* Full Name */}
          <SimpleTooltip
            content={t('extensions.contact.name.tooltip')}
            title={t('extensions.contact.name.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="contactName" className="text-left">
              {t('extensions.contact.name.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="contactName"
            value={contactInfo.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder={t('extensions.contact.name.placeholder')}
          />

          {/* Email */}
          <SimpleTooltip
            content={t('extensions.contact.email.tooltip')}
            title={t('extensions.contact.email.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="contactEmail" className="text-left">
              {t('extensions.contact.email.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="contactEmail"
            type="email"
            value={contactInfo.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder={t('extensions.contact.email.placeholder')}
          />

          {/* Mobile Phone */}
          <SimpleTooltip
            content={t('extensions.contact.phone.tooltip')}
            title={t('extensions.contact.phone.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="contactPhone" className="text-left">
              {t('extensions.contact.phone.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="contactPhone"
            type="tel"
            value={contactInfo.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder={t('extensions.contact.phone.placeholder')}
          />

          {/* Notes */}
          <SimpleTooltip
            content={t('extensions.contact.notes.tooltip')}
            title={t('extensions.contact.notes.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="contactNotes" className="text-left">
              {t('extensions.contact.notes.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="contactNotes"
            value={contactInfo.notes}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder={t('extensions.contact.notes.placeholder')}
          />
        </div>
      </FormBox>
    </div>
  );
}
